/**
 * `useCountryStatus` — TanStack Query hook for `regions.countriesProjection`.
 *
 * Returns the latest per-country hantavirus surveillance status rows produced
 * by the LLM extractor (prompt-04). Each row carries provenance fields
 * (`source_url`, `verified_at`) that the choropleth tooltip renders verbatim.
 *
 * Stale time: 60s — matches the server-side cache TTL. Polling interval:
 * 5 minutes — the extractor runs every 10 minutes, so a poll at half that
 * cadence catches new data without hammering the API.
 */

import { useQuery } from "@tanstack/react-query";

export type CountryStatusRow = {
  iso_a2: string;
  status: "confirmed" | "suspected" | "monitoring" | "cleared" | "unknown";
  detail: string | null;
  source_url: string;
  verified_at: string; // ISO-8601 string over the wire
};

async function fetchCountriesProjection(
  apiBaseUrl: string,
  locale: string,
): Promise<CountryStatusRow[]> {
  const base = apiBaseUrl.replace(/\/+$/u, "");
  const url = `${base}/api/trpc/regions.countriesProjection`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json", "x-locale": locale },
    credentials: "omit",
  });

  if (!res.ok) {
    throw new Error(`regions.countriesProjection failed: ${res.status}`);
  }

  const json = (await res.json()) as {
    result?: { data?: { json?: CountryStatusRow[] } | CountryStatusRow[] };
  };

  const inner = json.result?.data;
  const rows =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: CountryStatusRow[] }).json
      : (inner as CountryStatusRow[] | undefined);

  if (!rows) throw new Error("regions.countriesProjection: malformed response");
  return rows;
}

export function useCountryStatus(opts?: { apiBaseUrl?: string; locale?: string }) {
  const base =
    opts?.apiBaseUrl ??
    (typeof import.meta !== "undefined"
      ? (import.meta.env?.VITE_API_URL as string | undefined)
      : undefined) ??
    "";

  const locale = opts?.locale ?? "en";

  return useQuery<CountryStatusRow[]>({
    queryKey: ["regions", "countriesProjection", locale],
    queryFn: () => fetchCountriesProjection(base, locale),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: 2,
  });
}
